import styled from "styled-components";
import { motion, Variants } from "framer-motion";

import { SceneMenuButton } from "./SceneMenuButton";
import { Box } from "../Box";

import { DelayLink } from "../DelayLink";
import { PAGE_TRANSITION_DURATION } from "../../data/animationConfig";
import { ArtifactThemesContext } from "../../context/ArtifactsContext";
import { useContext, useState } from "react";
import { Portal } from "../Portal";
import { MenuOpenContext } from "../../context/MenuOpenContext";
import { FlexContainer, FlexItem } from "../Flex";
import { SceneLink } from "../SceneLink";

interface SceneMenuProps {
  hide?: boolean;
  guideShown: boolean;
  onSceneMenuToggle: () => any;
}

const Wrapper = styled(motion(Box))`
  position: fixed;
  z-index: 2;
  top: 100%;
  left: 0;
  width: 100%;
  pointer-events: none;
`;

const Inner = styled(motion(Box))`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
`;

const Container = styled(motion.div)`
  padding: 80px 16px 56px;
  height: 100%;
  overflow: auto;
  display: flex;
  align-items: center;
`;

const Nav = styled(motion.nav)`
  max-width: 1200px;
  margin: 0 auto;
`;

const MenuButtonPosition = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  pointer-events: all;
`;

const Background = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: ${({ theme }) => theme.colors.violet};
  z-index: -1;
  pointer-events: none;
`;

const Shadow = styled(motion.div)`
  position: absolute;
  top: -90vh;
  left: 0;
  width: 100%;
  height: 90vh;
  z-index: -1;
  box-shadow: 50px 20px 100px 0 rgba(0, 0, 0, 0.41);
  pointer-events: none;
`;

const hideVariants: Variants = {
  show: { opacity: 1, transition: { delay: 0.5 } },
  hide: { opacity: 0, transition: { duration: 0.2 } },
};
const innerVariants: Variants = {
  open: {
    y: "-100%",
    pointerEvents: "all",
    transition: {
      delay: 0.3,
      duration: 0.3,
      type: "tween",
    },
  },
  closed: {
    y: -80,
    pointerEvents: "none",
    transition: { delay: 0.3, duration: 0.3, type: "tween" },
  },
};

const bgVariants: Variants = {
  open: {
    opacity: 1,
    transition: { duration: 0.3, type: "tween" },
  },
  closed: {
    opacity: 0,
    transition: { delay: 0.6, duration: 0.3, type: "tween" },
  },
};

const navVariants: Variants = {
  open: {
    y: 0,
    opacity: 1,
    transition: { delay: 0.6, duration: 0.3, type: "tween" },
  },
  closed: {
    y: -20,
    opacity: 0,
    transition: { duration: 0.3, type: "tween" },
  },
};

export const SceneMenu: React.FC<SceneMenuProps> = ({
  hide,
  guideShown,
  onSceneMenuToggle,
}) => {
  const { artifactThemes } = useContext(ArtifactThemesContext);
  const { menuOpen } = useContext(MenuOpenContext);
  const [sceneMenuOpen, setSceneMenuOpen] = useState(false);

  const handleSceneMenuButtonClick = (): void => {
    setSceneMenuOpen(!sceneMenuOpen);
    onSceneMenuToggle();
  };

  const handleLinkClick = (): void => {
    setSceneMenuOpen(false);
  };

  return (
    <Portal>
      <motion.div variants={hideVariants} animate={menuOpen ? "hide" : "show"}>
        <Wrapper
          initial={false}
          animate={sceneMenuOpen ? "open" : "closed"}
          height={["calc(100% - 64px)", null, "80%", "60%"]}
        >
          <Inner
            variants={innerVariants}
            height={["calc(100% - 24px)", null, "100%"]}
          >
            <Background variants={bgVariants} />
            <Shadow variants={bgVariants} />
            <MenuButtonPosition>
              <motion.div>
                <SceneMenuButton
                  isAnimating={guideShown}
                  isOpen={false}
                  onClick={handleSceneMenuButtonClick}
                ></SceneMenuButton>
              </motion.div>
            </MenuButtonPosition>
            <Container>
              <Nav variants={navVariants}>
                <FlexContainer
                  flexWrap="wrap"
                  margin={[-2, null, null, null, -3]}
                >
                  {artifactThemes.map((artifactTheme) => (
                    <FlexItem
                      key={artifactTheme.id}
                      flex={["0 0 100%", null, "0 0 50%"]}
                      padding={[2, null, null, null, 3]}
                    >
                      <DelayLink
                        delay={PAGE_TRANSITION_DURATION * 1000}
                        onDelayStart={handleLinkClick}
                        to={"/themes/" + artifactTheme.slug}
                      >
                        <motion.div whileHover={{ scale: 1.02 }}>
                          <SceneLink
                            label={artifactTheme.name}
                            iconColor="violetLight"
                          ></SceneLink>
                        </motion.div>
                      </DelayLink>
                    </FlexItem>
                  ))}
                </FlexContainer>
              </Nav>
            </Container>
          </Inner>
        </Wrapper>
      </motion.div>
    </Portal>
  );
};
